import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ArrowDown2, ArrowRight2, Edit2 } from "iconsax-react";
import { Badge, Table } from "@/shared/ui";
import { STATUS_MAP } from "@/features/hr/employees/consts/statuses";
import { formatTrialPeriodEnd } from "@/features/hr/employees/utils/trialPeriod";
import { groupEmployeesByDepartment } from "@/features/hr/employees/utils/departmentGroups";
import { formatDateForDisplay, formatMoneyKzt, type Locale } from "@/shared/utils";
import type { WorkerListItem, ImportWorkerDraftListItem } from "../../../types";
import type { EmployeesSortKey, EmployeesSortConfig } from "../../../hooks";
import EmployeeActionsDropdown from "./EmployeeActionsDropdown";
import EmployeeNameCell from "./EmployeeNameCell";
import EmployeeAccessRoleIcons from "./EmployeeAccessRoleIcons";
import DraftNameCell from "./DraftNameCell";
import DraftActionsDropdown from "./DraftActionsDropdown";

interface Props {
  employees: WorkerListItem[];
  importDrafts?: ImportWorkerDraftListItem[];
  sortConfig: EmployeesSortConfig | null;
  onSort: (key: EmployeesSortKey) => void;
  onDownloadProfile: (id: number) => void;
  isDownloading: boolean;
  onOpenAssignRole: (employee: WorkerListItem) => void;
  onRehire: (employee: WorkerListItem) => void;
  isRehiring: boolean;
  onSelectEmployee: (employee: WorkerListItem) => void;
  onDeleteDraft?: (id: number) => void;
}

export default function EmployeesTableView({
  employees,
  importDrafts,
  sortConfig,
  onSort,
  onDownloadProfile,
  isDownloading,
  onOpenAssignRole,
  onRehire,
  isRehiring,
  onSelectEmployee,
  onDeleteDraft,
}: Props) {
  const { t, i18n } = useTranslation("EmployeesPage");
  const locale = (i18n.language as Locale) || "ru";
  const navigate = useNavigate();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);

  const groups = useMemo(
    function buildGroups() {
      return groupEmployeesByDepartment(employees);
    },
    [employees],
  );

  // The dropdown flips upwards on the very last row, so it needs the index
  // across the whole body, not inside the group the row happens to sit in.
  const rowIndexById = useMemo(
    function buildRowIndex() {
      const index = new Map<number, number>();
      groups
        .flatMap(function collectWorkers(group) {
          return group.workers;
        })
        .forEach(function assignIndex(worker, position) {
          index.set(worker.id, position);
        });
      return index;
    },
    [groups],
  );

  function toggleGroup(key: string) {
    setCollapsedGroups(function toggle(previous) {
      return previous.includes(key) ? previous.filter((item) => item !== key) : [...previous, key];
    });
  }

  function renderEmployeeRow(emp: WorkerListItem) {
    const index = rowIndexById.get(emp.id) ?? 0;
    const startDate = emp.active_contract?.start_date ? formatDateForDisplay(emp.active_contract.start_date) : "—";
    const position = emp.active_contract?.job_position_ru || emp.active_contract?.work_position || "—";
    const salaryNum = Number(emp.active_contract?.salary_amount ?? 0);
    const salaryFormatted = salaryNum ? formatMoneyKzt(salaryNum, locale) : "—";
    const statusConfig = STATUS_MAP[emp.status as keyof typeof STATUS_MAP];

    return (
      <Table.Row key={emp.id} onClick={() => onSelectEmployee(emp)} className="cursor-pointer">
        <Table.Cell>
          <EmployeeNameCell employee={emp} />
        </Table.Cell>

        <Table.Cell>
          {statusConfig ? (
            <Badge variant="soft" color={statusConfig.color} text={t(statusConfig.label)} icon={statusConfig.icon} />
          ) : (
            "—"
          )}
        </Table.Cell>

        <Table.Cell>{startDate}</Table.Cell>

        <Table.Cell>
          <div className="flex flex-col">
            <span>{position}</span>
            {emp.trial_period_end_date && (
              <span className="text-body-regular-xs content-base-secondary">
                {formatTrialPeriodEnd(emp.trial_period_end_date, t)}
              </span>
            )}
          </div>
        </Table.Cell>

        <Table.Cell>{salaryFormatted}</Table.Cell>

        <Table.Cell>
          <EmployeeAccessRoleIcons roles={emp.roles ?? []} locale={locale} tooltipAlign="right" />
        </Table.Cell>

        <Table.Cell>
          <EmployeeActionsDropdown
            employee={emp}
            onDownloadProfile={onDownloadProfile}
            isDownloading={isDownloading}
            onOpenAssignRole={onOpenAssignRole}
            onRehire={onRehire}
            isRehiring={isRehiring}
            direction={index === employees.length - 1 ? "top" : "bottom"}
          />
        </Table.Cell>
      </Table.Row>
    );
  }

  return (
    <div className="w-full min-w-0">
      <Table.Table scrollable tableClassName="min-w-[950px]">
        <Table.Header>
          <tr>
            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "full_name" ? sortConfig.direction : undefined}
              onSort={() => onSort("full_name")}>
              {t("table.fullName")}
            </Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "status" ? sortConfig.direction : undefined}
              onSort={() => onSort("status")}>
              {t("table.status")}
            </Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "start_date" ? sortConfig.direction : undefined}
              onSort={() => onSort("start_date")}>
              {t("table.hireDate")}
            </Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "work_position" ? sortConfig.direction : undefined}
              onSort={() => onSort("work_position")}>
              {t("table.position")}
            </Table.HeadCell>

            <Table.HeadCell
              sortable
              sortDirection={sortConfig?.key === "work_salary" ? sortConfig.direction : undefined}
              onSort={() => onSort("work_salary")}>
              {t("table.salary")}
            </Table.HeadCell>

            <Table.HeadCell>{t("table.role")}</Table.HeadCell>

            <Table.HeadCell>{t("table.actions")}</Table.HeadCell>
          </tr>
        </Table.Header>

        <Table.Body>
          {importDrafts?.map(function renderDraftRow(draft) {
            const position = locale === "kk" ? (draft.job_position_kk || draft.job_position_ru) : draft.job_position_ru;
            return (
              <Table.Row
                key={`draft-${draft.id}`}
                onClick={() => navigate(`/hr/import-worker?draftId=${draft.id}`)}
                className="cursor-pointer">
                <Table.Cell>
                  <DraftNameCell draft={draft} />
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    variant="soft"
                    color="gray"
                    text={t("status.draft")}
                    icon={<Edit2 size={14} color="currentColor" />}
                  />
                </Table.Cell>
                <Table.Cell>{formatDateForDisplay(draft.updated_at)}</Table.Cell>
                <Table.Cell>{position || "—"}</Table.Cell>
                <Table.Cell>{"—"}</Table.Cell>
                <Table.Cell>{"—"}</Table.Cell>
                <Table.Cell>
                  {onDeleteDraft && <DraftActionsDropdown draftId={draft.id} onDeleteDraft={onDeleteDraft} />}
                </Table.Cell>
              </Table.Row>
            );
          })}
          {groups.map(function renderGroup(group) {
            const isCollapsed = collapsedGroups.includes(group.key);
            const groupLabel = group.departmentId === null ? t("table.noDepartment") : group.name;

            return (
              <Fragment key={group.key}>
                <Table.Row selected>
                  <Table.Cell colSpan={7}>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? t("table.expandGroup") : t("table.collapseGroup")}
                        onClick={() => toggleGroup(group.key)}
                        className="flex items-center gap-3 min-w-0 cursor-pointer radius-xs outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-brand-200">
                        <span className="content-base-secondary shrink-0">
                          {isCollapsed ? (
                            <ArrowRight2 size={16} color="currentColor" />
                          ) : (
                            <ArrowDown2 size={16} color="currentColor" />
                          )}
                        </span>
                        {group.code && (
                          <span className="shrink-0 px-2 py-0.5 radius-xs text-body-bold-xs content-base-secondary [tr:nth-child(odd)_&]:bg-grey-50 [tr:nth-child(even)_&]:bg-white dark:[tr:nth-child(odd)_&]:bg-grey-800 dark:[tr:nth-child(even)_&]:bg-grey-950">
                            {group.code}
                          </span>
                        )}
                        <span className="text-body-bold-md content-base-primary truncate">{groupLabel}</span>
                      </button>
                    </div>
                  </Table.Cell>
                </Table.Row>

                {!isCollapsed && group.workers.map(renderEmployeeRow)}
              </Fragment>
            );
          })}
        </Table.Body>
      </Table.Table>
    </div>
  );
}
